/**
 * Session form (welcome meeting).
 */
import * as React from 'react';
import { useQuery } from '@tanstack/react-query';
import { X } from 'lucide-react';
import { SlidePanelForm } from '@/components/common/SlidePanelForm';
import { toLocalDateString } from '@/utils/format-date-utils';
import { Autocomplete } from '@/components/forms/Autocomplete';
import { DatePicker } from '@/components/forms/DatePicker';
import { TimePicker } from '@/components/forms/TimePicker';
import { Select } from '@/components/forms/Select';
import { Textarea } from '@/components/forms/Textarea';
import { UpdateRecurrenceScopeModal } from '@/components/sessions/remote/UpdateRecurrenceScopeModal';
import { useAdminAutocomplete } from '@/hooks/useAdminAutocomplete';
import { useAuthStore } from '@/stores/authStore';
import { previewRecurrence } from '@/services/remote/sessions.service';
import { listScoped as listStudentsRemoteScoped } from '@/services/remote/students.service';
import { getSessionTypesForTab } from '@/utils/common-utils';
import {
  SESSION_REMOTE_FORM_QUERY_KEYS,
  SESSION_REMOTE_MEETING_RECURRENCE_SELECT_OPTIONS,
} from '@/components/sessions/session-configs';
import { useCreateSession, useUpdateSession } from '@/hooks/useSessionsRemote';
import { useFormValidation } from '@/hooks/useFormValidation';
import {
  createSessionRemoteValidationSchema,
  editSessionRemoteValidationSchema,
} from '@/utils/validation-schemas-utils';
import {
  addOneHour,
  animatorRoleCodesForTab,
  appendUniqueById,
  buildCreateSessionDto,
  buildCreateValidationContext,
  buildEditValidationContext,
  buildMeetingRecurrenceFields,
  buildUpdateDto,
  computeParticipantIds,
  firstCreatedSessionId,
  formatMeetingRecurrencePreview,
  getMinTimeForDate,
  initializeFormState,
  isMeetingRecurrencePreviewEnabled,
  isRecurringRecurrenceType,
  mapScopedUsersToParticipantOptions,
  registerPostCreateParticipants,
  removeById,
  todayDateString,
} from '@/components/sessions/remote/session-form-utils';
import type { ParticipantChip, SessionFormRecord } from '@/types/remote/session.types';
import type { SelectedUser } from '@/types/user.types';
import { toast } from '@/components/ui/toast';
import { getErrorMessage } from '@/utils/errors-utils';

/** Remote tab this form creates and edits. */
const SESSION_TAB = 'welcome-meeting' as const;

/** Props for {@link WelcomeMeetingForm} (`create` vs `edit` discriminated). */
export type WelcomeMeetingFormProps =
  | {
      mode: 'create';
      onClose: () => void;
      onSuccess: () => void;
    }
  | {
      mode: 'edit';
      session: SessionFormRecord;
      sessionId: string;
      onClose: () => void;
      onSuccess: () => void;
    };

/** Session form (welcome meeting). */
export const WelcomeMeetingForm: React.FC<WelcomeMeetingFormProps> = (props) => {
  if (props.mode === 'edit') {
    return <EditWelcomeMeetingForm {...props} />;
  }
  return <CreateWelcomeMeetingForm {...props} />;
};

/** Create form for a remote welcome meeting. */
function CreateWelcomeMeetingForm({
  onClose,
  onSuccess,
}: Readonly<Extract<WelcomeMeetingFormProps, { mode: 'create' }>>) {
  const createMutation = useCreateSession();
  const currentUser = useAuthStore((state) => state.user);
  const validation = useFormValidation(createSessionRemoteValidationSchema);
  const availableTypes = getSessionTypesForTab(SESSION_TAB);
  const defaultType = availableTypes[0];

  const [selectedType, setSelectedType] = React.useState(defaultType?.type || '');
  const [sessionDate, setSessionDate] = React.useState('');
  const [startTime, setStartTime] = React.useState('');
  const [endTime, setEndTime] = React.useState('');
  const [recurrenceType, setRecurrenceType] = React.useState('none');
  const [customDates, setCustomDates] = React.useState<string[]>([]);
  const [customDateInput, setCustomDateInput] = React.useState('');
  const [singleAnimatorId, setSingleAnimatorId] = React.useState('');
  const [selectedStudentParticipants, setSelectedStudentParticipants] = React.useState<
    SelectedUser[]
  >([]);
  const [studentParticipantAutocompleteValue, setStudentParticipantAutocompleteValue] =
    React.useState('');
  const [studentParticipantResetKey, setStudentParticipantResetKey] = React.useState(0);
  const [internalNotes, setInternalNotes] = React.useState('');

  const {
    options: animatorOptions,
    isLoading: animatorsLoading,
    onSearch: onAnimatorSearch,
  } = useAdminAutocomplete({ roleCodes: animatorRoleCodesForTab(SESSION_TAB) });

  const [studentParticipantsSearchQuery, setStudentParticipantsSearchQuery] = React.useState('');
  const { data: studentParticipantsData, isLoading: studentParticipantsLoading } = useQuery({
    queryKey: SESSION_REMOTE_FORM_QUERY_KEYS.studentParticipants(
      studentParticipantsSearchQuery,
      SESSION_TAB,
    ),
    queryFn: () =>
      listStudentsRemoteScoped({
        search: studentParticipantsSearchQuery,
        participationRA: false,
        notReservedForUpcomingRA: true,
        pageSize: 10,
      }),
  });
  const studentParticipantOptions = React.useMemo(
    () =>
      mapScopedUsersToParticipantOptions(
        studentParticipantsData?.data || [],
        selectedStudentParticipants.map((sel) => sel.id),
      ),
    [studentParticipantsData?.data, selectedStudentParticipants],
  );
  const onStudentParticipantsSearch = React.useCallback((query: string) => {
    setStudentParticipantsSearchQuery(query);
  }, []);

  const validationCtx = buildCreateValidationContext({
    selectedType,
    sessionDate,
    startTime,
    tutorId: '',
    studentId: '',
    managedById: null,
    recurrenceType,
    endTime,
    programId: '',
    eventType: '',
    sessionType: SESSION_TAB,
    isPrefilledFromMatching: false,
    animatorsCount: 0,
    singleAnimatorId,
  });

  React.useEffect(() => {
    for (const field of Object.keys(createSessionRemoteValidationSchema)) {
      validation.validateField(
        field,
        validationCtx[field as keyof typeof validationCtx],
        validationCtx,
      );
    }
  }, [selectedType, sessionDate, startTime, endTime, singleAnimatorId, recurrenceType]);

  const todayStr = todayDateString();
  /** Changing start time fills end with start + 1h when end is empty. */
  const handleStartTimeChange = (value: string) => {
    setStartTime(value);
    if (value) setEndTime(addOneHour(value));
  };

  /** Append a unique student-participant chip from the autocomplete value. */
  const handleAddStudentParticipant = (value: string) => {
    if (!value || selectedStudentParticipants.some((s) => s.id === value)) return;
    const option = studentParticipantOptions.find((o) => o.value === value);
    if (option) {
      setSelectedStudentParticipants((prev) =>
        appendUniqueById(prev, { id: value, label: option.label }),
      );
    }
    setStudentParticipantAutocompleteValue('');
    setStudentParticipantResetKey((k) => k + 1);
  };

  const handleAddCustomDate = React.useCallback(() => {
    if (!customDateInput) return;
    const alreadyExists = customDates.includes(customDateInput) || customDateInput === sessionDate;
    if (alreadyExists) {
      toast.error('Cette date est déjà dans la liste.');
      return;
    }
    setCustomDates((prev) => [...prev, customDateInput].sort((a, b) => a.localeCompare(b)));
    setCustomDateInput('');
  }, [customDateInput, customDates, sessionDate]);

  const handleRemoveCustomDate = React.useCallback((date: string) => {
    setCustomDates((prev) => prev.filter((d) => d !== date));
  }, []);

  const createTypeMapping = availableTypes.find((t) => t.type === selectedType);
  const recurrenceFields = buildMeetingRecurrenceFields({
    uiType: recurrenceType,
    sessionDate,
    customDates,
  });
  const { data: recurrencePreview } = useQuery({
    queryKey: SESSION_REMOTE_FORM_QUERY_KEYS.recurrencePreview(
      createTypeMapping?.type ?? '',
      sessionDate,
      recurrenceFields.recurrenceType,
      JSON.stringify(recurrenceFields.recurrenceRule ?? null),
    ),
    queryFn: () =>
      previewRecurrence({
        type: createTypeMapping?.type,
        sessionDate,
        recurrenceType: recurrenceFields.recurrenceType,
        recurrenceRule: recurrenceFields.recurrenceRule,
      }),
    enabled:
      isMeetingRecurrencePreviewEnabled({ sessionDate, uiType: recurrenceType, customDates }) &&
      Boolean(createTypeMapping?.type),
  });

  /** Count sentence under the recurrence Select, or nothing. */
  function renderRecurrencePreview(): React.ReactNode {
    const text = formatMeetingRecurrencePreview(recurrencePreview);
    if (!text) return null;
    return <p className="text-sm text-gray-600">{text}</p>;
  }

  /** Validate, then POST create. */
  const handleSubmit = async () => {
    if (!validation.validateForm(validationCtx)) return;
    const mapping = availableTypes.find((t) => t.type === selectedType);
    if (!mapping) return;

    const dto = buildCreateSessionDto({
      mapping,
      sessionType: SESSION_TAB,
      sessionDate,
      startTime,
      endTime,
      recurrenceType,
      customDates,
      planDuringHolidays: false,
      tutorId: '',
      studentId: '',
      selectedTutorParticipants: [],
      selectedStudentParticipants,
      singleAnimatorId,
      selectedAnimators: [],
      managedById: null,
      maxParticipants: 0,
      internalNotes,
      eventType: '',
      location: '',
      budgetPosts: [],
      etpCount: 0,
      etpUnitCost: 0,
      budgetGrandTotal: 0,
      programId: '',
    });

    try {
      const createdSession = await createMutation.mutateAsync(dto as never);
      const sessionId = firstCreatedSessionId(createdSession);
      const companyId = currentUser?.companyId;
      if (companyId && sessionId) {
        await registerPostCreateParticipants({
          sessionType: SESSION_TAB,
          sessionId,
          companyId,
          selectedTutorParticipants: [],
          selectedStudentParticipants,
        });
      }
      toast.success('Séance créée avec succès !');
      onSuccess();
    } catch (err: unknown) {
      toast.error(getErrorMessage(err));
    }
  };

  return (
    <SlidePanelForm
      formType="create"
      title="Créer une réunion d'accueil"
      subtitle="Renseignez les informations de la réunion d'accueil"
      onClose={onClose}
      onSubmit={handleSubmit}
      submitLabel="Créer"
      isSubmitting={createMutation.isPending}
      isValid={validation.isFormValid()}
    >
      <div className="space-y-6 p-6">
        {availableTypes.length > 1 && (
          <div
            className="space-y-4 p-4 rounded-lg border"
            style={{ borderColor: 'var(--color-1-nuance-3)' }}
          >
            <h3 className="font-semibold" style={{ color: 'var(--color-1-nuance-1)' }}>
              Configuration de la séance
            </h3>
            <Select
              label="Type de séance"
              value={selectedType}
              onChange={setSelectedType}
              options={availableTypes.map((t) => ({ value: t.type, label: t.label }))}
            />
          </div>
        )}

        <div
          className="space-y-4 p-4 rounded-lg border"
          style={{ borderColor: 'var(--color-2-nuance-3)' }}
        >
          <h3 className="font-semibold" style={{ color: 'var(--color-2-nuance-1)' }}>
            Planification
          </h3>
          <DatePicker
            label="Date"
            required
            value={sessionDate}
            onChange={setSessionDate}
            minDate={todayStr}
          />
          <div className="grid grid-cols-2 gap-4">
            <TimePicker
              label="Heure de début"
              required
              value={startTime}
              onChange={handleStartTimeChange}
              minTime={getMinTimeForDate(sessionDate, todayStr)}
              step={1}
            />
            <TimePicker
              label="Heure de fin"
              value={endTime}
              onChange={setEndTime}
              minTime={startTime || undefined}
              step={1}
            />
          </div>
          <p className="text-sm text-gray-600 italic">
            Les horaires sont en heure de Paris (Europe/Paris).
          </p>
          <Select
            label="Récurrence"
            required
            value={recurrenceType}
            onChange={setRecurrenceType}
            options={SESSION_REMOTE_MEETING_RECURRENCE_SELECT_OPTIONS}
          />
          {renderRecurrencePreview()}
          {recurrenceType === 'custom' && (
            <div className="space-y-2">
              <div className="flex gap-2">
                <div className="flex-1">
                  <DatePicker
                    label="Ajouter une date"
                    value={customDateInput}
                    onChange={setCustomDateInput}
                    minDate={todayStr}
                  />
                </div>
                <button
                  type="button"
                  onClick={handleAddCustomDate}
                  className="mt-6 px-4 py-2 text-sm font-medium text-white rounded-lg"
                  style={{ backgroundColor: 'var(--color-2-nuance-1)' }}
                >
                  Ajouter
                </button>
              </div>
              {customDates.length > 0 && (
                <div className="flex flex-wrap gap-2">
                  {customDates.map((date) => (
                    <span
                      key={date}
                      className="inline-flex items-center gap-1 px-3 py-1 rounded-lg text-sm"
                      style={{
                        background: 'color-mix(in srgb, var(--color-2-nuance-1) 12%, transparent)',
                        color: 'var(--color-2-nuance-1)',
                        border: '1px solid var(--color-2-nuance-3)',
                      }}
                    >
                      {date}
                      <button
                        type="button"
                        onClick={() => handleRemoveCustomDate(date)}
                        className="ml-1 hover:opacity-70"
                      >
                        <X className="h-3 w-3" />
                      </button>
                    </span>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>

        <div
          className="space-y-4 p-4 rounded-lg border"
          style={{ borderColor: 'var(--color-3-nuance-3)' }}
        >
          <h3 className="font-semibold" style={{ color: 'var(--color-3-nuance-1)' }}>
            Informations générales
          </h3>
          <Autocomplete
            label="Animateur"
            required
            placeholder="Rechercher un animateur..."
            options={animatorOptions}
            value={singleAnimatorId}
            onChange={setSingleAnimatorId}
            onSearch={onAnimatorSearch}
            isLoading={animatorsLoading}
            minSearchLength={2}
          />
          <div>
            <span className="block text-sm font-medium mb-2">Élèves participants (optionnel)</span>
            <Autocomplete
              key={studentParticipantResetKey}
              placeholder="Rechercher un élève..."
              options={studentParticipantOptions}
              value={studentParticipantAutocompleteValue}
              onChange={handleAddStudentParticipant}
              onSearch={onStudentParticipantsSearch}
              isLoading={studentParticipantsLoading}
              minSearchLength={2}
              helperText="Seuls les élèves en cours d'inscription et non encore inscrits à une réunion d'accueil sont affichés."
            />
            {selectedStudentParticipants.length > 0 && (
              <div className="flex flex-wrap gap-2 mt-2">
                {selectedStudentParticipants.map((s) => (
                  <span
                    key={s.id}
                    className="inline-flex items-center gap-1 px-3 py-1 rounded-lg text-sm"
                    style={{
                      background: 'color-mix(in srgb, var(--color-3-nuance-1) 12%, transparent)',
                      color: 'var(--color-3-nuance-1)',
                      border: '1px solid var(--color-3-nuance-3)',
                    }}
                  >
                    {s.label}
                    <button
                      type="button"
                      onClick={() =>
                        setSelectedStudentParticipants((prev) => removeById(prev, s.id))
                      }
                      className="ml-1 hover:opacity-70"
                    >
                      <X className="w-3 h-3" />
                    </button>
                  </span>
                ))}
              </div>
            )}
          </div>
        </div>

        <div
          className="space-y-2 p-4 rounded-lg border"
          style={{ borderColor: 'var(--color-1-nuance-3)' }}
        >
          <Textarea
            label="Notes internes (optionnel)"
            value={internalNotes}
            onChange={(e) => setInternalNotes(e.target.value)}
            placeholder="Notes ou commentaires internes..."
            rows={3}
          />
        </div>
      </div>
    </SlidePanelForm>
  );
}

/** Edit form for an existing remote welcome meeting. */
function EditWelcomeMeetingForm({
  session,
  sessionId,
  onClose,
  onSuccess,
}: Readonly<Extract<WelcomeMeetingFormProps, { mode: 'edit' }>>) {
  const updateMutation = useUpdateSession();
  const validation = useFormValidation(editSessionRemoteValidationSchema);
  const [sessionDate, setSessionDate] = React.useState('');
  const [startTime, setStartTime] = React.useState('');
  const [endTime, setEndTime] = React.useState('');
  const [recurrenceType, setRecurrenceType] = React.useState('');
  const [internalNotes, setInternalNotes] = React.useState('');
  const [singleAnimatorId, setSingleAnimatorId] = React.useState('');
  const [singleAnimatorName, setSingleAnimatorName] = React.useState('');
  const [selectedStudentParticipants, setSelectedStudentParticipants] = React.useState<
    ParticipantChip[]
  >([]);
  const [studentParticipantResetKey, setStudentParticipantResetKey] = React.useState(0);
  const [isModalOpen, setIsModalOpen] = React.useState(false);

  const {
    options: animatorOptionsRaw,
    isLoading: isLoadingAnimators,
    onSearch: onSearchAnimators,
  } = useAdminAutocomplete({
    roleCodes: animatorRoleCodesForTab(SESSION_TAB),
  });
  const animatorOptions = React.useMemo(() => {
    if (
      singleAnimatorId &&
      singleAnimatorName &&
      !animatorOptionsRaw.some((o) => o.value === singleAnimatorId)
    ) {
      return [{ value: singleAnimatorId, label: singleAnimatorName }, ...animatorOptionsRaw];
    }
    return animatorOptionsRaw;
  }, [animatorOptionsRaw, singleAnimatorId, singleAnimatorName]);

  const [studentParticipantsSearchQuery, setStudentParticipantsSearchQuery] = React.useState('');
  const { data: studentParticipantsData, isLoading: isLoadingStudentParticipants } = useQuery({
    queryKey: SESSION_REMOTE_FORM_QUERY_KEYS.studentParticipants(
      studentParticipantsSearchQuery,
      session.type,
    ),
    queryFn: () =>
      listStudentsRemoteScoped({
        search: studentParticipantsSearchQuery,
        participationRA: false,
        notReservedForUpcomingRA: true,
        pageSize: 10,
      }),
  });
  const studentParticipantOptions = React.useMemo(
    () =>
      mapScopedUsersToParticipantOptions(
        studentParticipantsData?.data || [],
        selectedStudentParticipants.map((sel) => sel.id),
      ),
    [studentParticipantsData?.data, selectedStudentParticipants],
  );
  const onSearchStudentParticipants = React.useCallback((query: string) => {
    setStudentParticipantsSearchQuery(query);
  }, []);

  React.useEffect(() => {
    initializeFormState(session, {
      setSessionDate,
      setStartTime,
      setEndTime,
      setRecurrenceType,
      setPlanDuringHolidays: () => undefined,
      setInternalNotes,
      setMaxParticipants: () => undefined,
      setSelectedTutor: () => undefined,
      setSelectedStudent: () => undefined,
      setSingleAnimatorId,
      setSingleAnimatorName,
      setSelectedAnimators: () => undefined,
      setSelectedTutorParticipants: () => undefined,
      setSelectedStudentParticipants,
      setManagedById: () => undefined,
      setProgramId: () => undefined,
      setEventType: () => undefined,
      setLocation: () => undefined,
      setBudgetPosts: () => undefined,
      setEtpUnitCost: () => undefined,
    });
    validation.validateInitialFormData({
      sessionDate: toLocalDateString(new Date(session.sessionDate)),
      startTime: session.startTime,
      managedById: null,
      programId: '',
      _isTutoring: false,
    });
  }, [session]);

  React.useEffect(() => {
    const ctx = buildEditValidationContext({
      sessionDate,
      startTime,
      managedById: null,
      programId: '',
      isTutoring: false,
    });
    validation.validateField('sessionDate', sessionDate, ctx);
    validation.validateField('startTime', startTime, ctx);
  }, [sessionDate, startTime]);

  /** Set the single animator id and display name. */
  const handleSingleAnimatorChange = (val: string) => {
    setSingleAnimatorId(val);
    const opt = animatorOptions.find((o) => o.value === val);
    setSingleAnimatorName(opt?.label ?? '');
  };

  /** Validate, then POST create or PATCH edit. */
  const handleSubmit = async () => {
    const ctx = buildEditValidationContext({
      sessionDate,
      startTime,
      managedById: null,
      programId: '',
      isTutoring: false,
    });
    if (!validation.validateForm(ctx)) return;

    if (isRecurringRecurrenceType(session.recurrenceType)) {
      setIsModalOpen(true);
      return;
    }

    await executeUpdate(false);
  };

  const handleConfirmScope = async (scope: 'occurrence' | 'following') => {
    setIsModalOpen(false);
    await executeUpdate(scope === 'following');
  };

  const executeUpdate = async (updateFutureOccurrences: boolean) => {
    const { tutorIds, studentIds, animatorIds } = computeParticipantIds({
      session,
      isTutoring: false,
      selectedTutor: null,
      selectedStudent: null,
      selectedTutorParticipants: [],
      selectedStudentParticipants,
      selectedAnimators: [],
      singleAnimatorId,
    });

    const dto = buildUpdateDto({
      session,
      sessionDate,
      startTime,
      endTime,
      recurrenceType: session.recurrenceType || '',
      planDuringHolidays: false,
      internalNotes,
      maxParticipants: 0,
      tutorIds,
      studentIds,
      animatorIds,
      managedById: null,
      programId: '',
      eventType: '',
      location: '',
      budgetPosts: [],
      etpCount: 0,
      etpUnitCost: 0,
      budgetGrandTotal: 0,
      updateFutureOccurrences,
    });

    try {
      await updateMutation.mutateAsync({ id: sessionId, dto });
      toast.success('Séance modifiée avec succès !');
      onSuccess();
    } catch (err: unknown) {
      toast.error(getErrorMessage(err));
    }
  };

  return (
    <SlidePanelForm
      formType="edit"
      title="Modifier la réunion d'accueil"
      subtitle="Modifiez les informations de la réunion d'accueil"
      onClose={onClose}
      onSubmit={handleSubmit}
      submitLabel="Enregistrer"
      isSubmitting={updateMutation.isPending}
      isValid={validation.isFormValid()}
    >
      <div className="space-y-6 p-6">
        <div
          className="space-y-4 p-4 rounded-lg border"
          style={{ borderColor: 'var(--color-2-nuance-3)' }}
        >
          <h3 className="font-semibold text-lg" style={{ color: 'var(--color-2-nuance-1)' }}>
            Planification
          </h3>
          <DatePicker label="Date" value={sessionDate} onChange={setSessionDate} required />
          <div className="grid grid-cols-2 gap-4">
            <TimePicker
              label="Heure de début"
              value={startTime}
              onChange={setStartTime}
              required
              step={1}
            />
            <TimePicker label="Heure de fin" value={endTime} onChange={setEndTime} step={1} />
          </div>
          <p className="text-sm text-gray-600 italic">
            Les horaires sont en heure de Paris (Europe/Paris).
          </p>
          <Select
            label="Récurrence"
            value={recurrenceType}
            onChange={() => {}}
            options={SESSION_REMOTE_MEETING_RECURRENCE_SELECT_OPTIONS}
            disabled
          />
        </div>

        <div
          className="space-y-4 p-4 rounded-lg border"
          style={{ borderColor: 'var(--color-1-nuance-3)' }}
        >
          <h3 className="font-semibold text-lg" style={{ color: 'var(--color-1-nuance-1)' }}>
            Animateur
          </h3>
          <Autocomplete
            label="Animateur"
            placeholder="Rechercher un animateur..."
            options={animatorOptions}
            value={singleAnimatorId}
            onChange={handleSingleAnimatorChange}
            onSearch={onSearchAnimators}
            isLoading={isLoadingAnimators}
            minSearchLength={2}
            required
          />
        </div>

        <div
          className="space-y-4 p-4 rounded-lg border"
          style={{ borderColor: 'var(--color-3-nuance-3)' }}
        >
          <h3 className="font-semibold text-lg" style={{ color: 'var(--color-3-nuance-1)' }}>
            Élèves participants ({selectedStudentParticipants.length})
          </h3>
          <Autocomplete
            key={studentParticipantResetKey}
            placeholder="Ajouter un élève participant..."
            options={studentParticipantOptions}
            value=""
            onChange={(val) => {
              const option = studentParticipantOptions.find((o) => o.value === val);
              if (option && !selectedStudentParticipants.some((s) => s.id === option.value)) {
                setSelectedStudentParticipants((prev) => [
                  ...prev,
                  { id: option.value, name: option.label, role: 'student' },
                ]);
                setStudentParticipantResetKey((k) => k + 1);
              }
            }}
            onSearch={onSearchStudentParticipants}
            isLoading={isLoadingStudentParticipants}
            minSearchLength={2}
            helperText="Seuls les élèves en cours d'inscription et non encore inscrits à une réunion d'accueil sont affichés."
          />
          {selectedStudentParticipants.length > 0 && (
            <div className="flex flex-wrap gap-2 mt-2">
              {selectedStudentParticipants.map((student) => (
                <div
                  key={student.id}
                  className="inline-flex items-center gap-2 px-3 py-1.5 rounded-lg bg-[var(--color-3-nuance-1)] text-white"
                >
                  <span className="text-sm">{student.name}</span>
                  <button
                    type="button"
                    onClick={() =>
                      setSelectedStudentParticipants((prev) => removeById(prev, student.id))
                    }
                    className="hover:bg-white/20 rounded p-0.5"
                  >
                    <X className="w-3.5 h-3.5" />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

        <div
          className="space-y-4 p-4 rounded-lg border"
          style={{ borderColor: 'var(--color-1-nuance-3)' }}
        >
          <h3 className="font-semibold text-lg" style={{ color: 'var(--color-1-nuance-1)' }}>
            Notes internes
          </h3>
          <Textarea
            value={internalNotes}
            onChange={(e) => setInternalNotes(e.target.value)}
            placeholder="Notes ou commentaires internes..."
            rows={3}
          />
        </div>
      </div>
      <UpdateRecurrenceScopeModal
        open={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onConfirm={handleConfirmScope}
      />
    </SlidePanelForm>
  );
}
